import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Spin,
  Table,
  Tag,
  Select,
  notification,
} from "antd";
import queryString from "query-string";
import {
  assignSpecialDeliveryTour,
  fetchSpecialOrders,
  fetchSpecialDeliveryFleet,
} from "../redux/actions/specialDeliveryActions";
import SpecialDeliveryNav from "./SpecialDeliveryNav";

const { Title, Paragraph } = Typography;
const { Option } = Select;

const pageStyle = {
  minHeight: "100vh",
  background: "#f0f2f5",
  padding: "32px 24px",
};

const extractErrorMessage = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  return data.message || "";
};

const statusColor = (status) => {
  if (status === "OPEN") return "green";
  if (!status) return "default";
  return "orange";
};

const SpecialPlanningBoard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { authToken, clientId } = queryString.parse(location.search);

  const specialOrders = useSelector(
    (state) => state?.specialDelivery?.specialOrders || []
  );
  const fleet = useSelector(
    (state) =>
      state?.specialDelivery?.fleet || {
        vehicles: [],
        drivers: [],
        transporters: [],
      }
  );

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [vehicleId, setVehicleId] = useState(undefined);
  const [driverId, setDriverId] = useState(undefined);
  const [helperId, setHelperId] = useState(undefined);
  const [isAssigning, setIsAssigning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [boardError, setBoardError] = useState("");

  const authParams = { clientId, authToken };

  const handleError = useCallback((error = {}) => {
    const { data = {} } = error;
    setBoardError(extractErrorMessage(data) || "Something went wrong.");
  }, []);

  const loadBoard = useCallback(() => {
    setIsLoading(true);
    setBoardError("");
    Promise.all([
      dispatch(fetchSpecialOrders(authParams, navigate, handleError)),
      dispatch(fetchSpecialDeliveryFleet(authParams, navigate, handleError)),
    ]).finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, authToken]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  const columns = [
    { title: "Order ID", dataIndex: "orderId", key: "orderId" },
    { title: "Sub-category", dataIndex: "subCategory", key: "subCategory" },
    { title: "Reason Code", dataIndex: "reasonCode", key: "reasonCode" },
    {
      title: "Status",
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (status) => (
        <Tag color={statusColor(status)}>{status || "UNKNOWN"}</Tag>
      ),
    },
  ];

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => ({
      disabled: record.orderStatus && record.orderStatus !== "OPEN",
    }),
  };

  const canConfirm =
    selectedRowKeys.length > 0 && Boolean(vehicleId) && Boolean(driverId);

  const onConfirm = () => {
    setBoardError("");
    const requestObject = {
      orderIds: selectedRowKeys,
      vehicleId,
      driverId,
      helperId,
    };

    setIsAssigning(true);

    dispatch(
      assignSpecialDeliveryTour(requestObject, authParams, navigate, handleError)
    )
      .then((res) => {
        if (res) {
          notification.success({
            message: "Special delivery tour assigned",
            placement: "bottomRight",
          });
          setSelectedRowKeys([]);
          setVehicleId(undefined);
          setDriverId(undefined);
          setHelperId(undefined);
          loadBoard();
          return;
        }
        setBoardError(
          (prev) => prev || "Could not assign the tour. Please try again."
        );
      })
      .catch(() => {
        setBoardError(
          (prev) => prev || "Could not assign the tour. Please try again."
        );
      })
      .finally(() => {
        setIsAssigning(false);
      });
  };

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <SpecialDeliveryNav />
        <Card
          style={{ marginTop: 16, borderRadius: 8 }}
          bodyStyle={{ padding: 32 }}
        >
          <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
            Special Delivery Planning
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 24 }}>
            Select OPEN special orders and assign them to a vehicle, driver and
            optional helper.
          </Paragraph>

          {boardError ? (
            <Alert
              type="error"
              showIcon
              closable
              message={boardError}
              onClose={() => setBoardError("")}
              style={{ marginBottom: 24 }}
            />
          ) : null}

          <Spin spinning={isLoading}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Row gutter={[16, 16]} align="bottom">
                <Col>
                  <div data-testid="vehicle-select">
                    <Select
                      virtual={false}
                      showSearch
                      optionFilterProp="children"
                      aria-label="Select vehicle"
                      style={{ width: 220 }}
                      placeholder="Select vehicle"
                      value={vehicleId}
                      onChange={(value) => setVehicleId(value)}
                      notFoundContent="No vehicles"
                    >
                      {fleet.vehicles.map((vehicle) => (
                        <Option key={vehicle.id} value={vehicle.id}>
                          {vehicle.name || vehicle.id}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col>
                  <div data-testid="driver-select">
                    <Select
                      virtual={false}
                      showSearch
                      optionFilterProp="children"
                      aria-label="Select driver"
                      style={{ width: 220 }}
                      placeholder="Select driver"
                      value={driverId}
                      onChange={(value) => setDriverId(value)}
                      notFoundContent="No drivers"
                    >
                      {fleet.drivers.map((driver) => (
                        <Option key={driver.id} value={driver.id}>
                          {driver.name || driver.id}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col>
                  <div data-testid="helper-select">
                    <Select
                      virtual={false}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      aria-label="Select helper"
                      style={{ width: 220 }}
                      placeholder="Select helper (optional)"
                      value={helperId}
                      onChange={(value) => setHelperId(value)}
                      notFoundContent="No helpers"
                    >
                      {fleet.drivers.map((helper) => (
                        <Option key={helper.id} value={helper.id}>
                          {helper.name || helper.id}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    disabled={!canConfirm || isAssigning}
                    loading={isAssigning}
                    onClick={onConfirm}
                  >
                    Assign tour
                  </Button>
                </Col>
              </Row>

              <Table
                bordered
                rowKey={(record) => record.orderId ?? record.id}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={specialOrders}
                pagination={false}
                locale={{ emptyText: "No special orders to plan yet." }}
              />
            </Space>
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default SpecialPlanningBoard;
